<template>
  <section ref="fsRoot" class="maquina-estado-root">

    <!-- TOOLBAR -->
    <div class="toolbar-row">
      <div class="toolbar-left">
        <div class="toolbar-title">
          <v-icon size="16" color="#5A6476">mdi-clipboard-list-outline</v-icon>
          Estado de ODT por Máquina
        </div>
        <div v-if="!loading" class="machine-count-pill">
          {{ maquinasFiltradas.length }} máquinas
        </div>
        <div class="machine-count-pill">
          Siguiente actualizacion: {{ nextUpdateLabel }}
        </div>
        <div v-if="isFullscreen && totalPagesFullscreen > 1" class="machine-count-pill">
          Pagina {{ currentPage + 1 }} de {{ totalPagesFullscreen }}
        </div>
      </div>
      <button class="fs-btn" @click="extenderPantalla">
        <v-icon size="16" class="mr-1">{{ fsIcon }}</v-icon>
        <span class="fs-btn-label">{{ fsLabel }}</span>
      </button>
    </div>

    <v-card id="projection-odt" elevation="0" class="dashboard-card">
      <v-card-text class="dashboard-body">

        <!-- FILTRO DE AREA -->
        <v-autocomplete v-model="idArea" :items="areas" label="Área" item-value="idArea" item-text="nombre" hide-details
          clearable outlined dense class="area-select mb-4" />

        <!-- FILTROS DE ESTADO (chips) -->
        <div class="filter-chips mb-5">
          <button v-for="(filtro, idx) in filtros" :key="'f-' + idx" class="filter-chip"
            :class="{ active: filtroValor === idx }" :style="filtroValor === idx ? activeChipStyle(filtro.opcion) : {}"
            @click="filtroValor = idx">
            <span v-if="filtro.opcion !== null" class="chip-dot"
              :style="{ background: colorCard(filtro.opcion) }"></span>
            {{ filtro.nombre }}
            <span class="chip-count">{{ contarFiltro(filtro.opcion) }}</span>
          </button>
        </div>

        <!-- LOADING -->
        <v-progress-linear v-if="loading" indeterminate color="primary" rounded class="mb-4" />

        <div v-if="loading && maquinas.length === 0" class="loading-state">
          <v-progress-circular :size="64" :width="5" color="primary" indeterminate />
          <p class="loading-text">Cargando ODT de máquinas...</p>
        </div>

        <!-- GRID DE CARDS -->
        <v-row v-if="!loading || maquinas.length > 0" justify="start" align="stretch">
          <v-col v-for="maquina in maquinasPagina" :key="'maq-' + maquina.idMaquinaIns" :cols="12" :sm="dynamicCols.sm"
            :md="dynamicCols.md" :lg="dynamicCols.lg" :xl="dynamicCols.xl" class="d-flex" style="padding: 10px">
            <CardODTMaquina :item="maquina" style="width:100%" />
          </v-col>
        </v-row>

      </v-card-text>
    </v-card>
  </section>
</template>

<script>
import CardODTMaquina from "./MaquinaODTEstado/CardODTMaquina.vue";
import axios from "axios";

const STATUS_COLORS = {
  1: '#00C472', // con ODT
  2: '#8A95A3', // sin ODT
};

export default {
  name: 'EstadoODTMaquinas',
  components: { CardODTMaquina },

  props: {
    active: { type: Boolean, default: true },
    pollMs: { type: Number, default: 180000 },
  },

  data: () => ({
    isFullscreen: false,

    filtros: [
      { opcion: null, nombre: 'Todas' },
      { opcion: 1, nombre: 'Con ODT' },
      { opcion: 2, nombre: 'Sin ODT' },
    ],
    filtroValor: 0,

    loading: false,
    maquinas: [],
    instancias: [],
    areas: [],
    idArea: -1,

    interval: null,
    intervalCountdown: null,
    intervalPage: null,
    nextUpdateSeconds: 180,
    currentPage: 0,
    pageSizeFullscreen: 12,

    montado: false,
  }),

  watch: {
    active(n) { n ? this.iniciar() : this.detener(); },
    idArea() { this.actualizarTodo(); },
    filtroValor() { this.normalizarPaginaActual(); },
    maquinasFiltradas() {
      this.normalizarPaginaActual();
      if (this.isFullscreen && this.totalPagesFullscreen > 1) this.iniciarPaginadoFullscreen();
      if (this.totalPagesFullscreen <= 1) this.detenerPaginadoFullscreen();
    },
    isFullscreen(n) {
      this.currentPage = 0;
      n ? this.iniciarPaginadoFullscreen() : this.detenerPaginadoFullscreen();
    },
  },

  computed: {
    fsLabel() { return this.isFullscreen ? 'Salir de Pantalla' : 'Extender Pantalla'; },
    fsIcon() { return this.isFullscreen ? 'mdi-arrow-collapse-all' : 'mdi-arrow-expand-all'; },

    nextUpdateLabel() {
      const total = Math.max(0, Number(this.nextUpdateSeconds) || 0);
      const minutes = Math.floor(total / 60);
      const seconds = total % 60;
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    },

    maquinasFiltradas() {
      const opcion = this.filtros[this.filtroValor]?.opcion ?? null;
      const maquinas = this.maquinas.filter(m => {
        if (opcion === 1) return m.status === 1;
        if (opcion === 2) return m.status === 2;
        return true;
      });
      // Activas primero, luego por nombre de máquina.
      return [...maquinas].sort((a, b) => {
        if (a.status !== b.status) return a.status - b.status;
        return String(a.codigoMaquina || '').localeCompare(String(b.codigoMaquina || ''), 'es', { numeric: true });
      });
    },

    maquinasPagina() {
      if (!this.isFullscreen) return this.maquinasFiltradas;
      const start = this.currentPage * this.pageSizeFullscreen;
      return this.maquinasFiltradas.slice(start, start + this.pageSizeFullscreen);
    },

    totalPagesFullscreen() {
      if (!this.isFullscreen) return 1;
      return Math.max(1, Math.ceil(this.maquinasFiltradas.length / this.pageSizeFullscreen));
    },

    dynamicCols() {
      const total = this.maquinasPagina.length;
      if (this.isFullscreen) {
        if (total <= 2) return { sm: 12, md: 6, lg: 6, xl: 6 };
        if (total <= 4) return { sm: 12, md: 6, lg: 4, xl: 3 };
        return { sm: 12, md: 4, lg: 3, xl: 2 };
      }
      return { sm: 12, md: 6, lg: 4, xl: 4 };
    },
  },

  mounted() {
    this.montado = true;
    if (this.active) this.iniciar();
    const sync = () => {
      this.isFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement
      );
    };
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync);
    document.addEventListener('MSFullscreenChange', sync);
    this._syncFs = sync;
  },

  beforeDestroy() {
    this.montado = false;
    this.detener();
    if (this._syncFs) {
      document.removeEventListener('fullscreenchange', this._syncFs);
      document.removeEventListener('webkitfullscreenchange', this._syncFs);
      document.removeEventListener('MSFullscreenChange', this._syncFs);
    }
  },

  methods: {
    /* ── FULLSCREEN ── */
    async extenderPantalla() {
      const el = this.$refs.fsRoot;
      if (!document.fullscreenElement) {
        await el.requestFullscreen?.();
        this.isFullscreen = true;
      } else {
        await document.exitFullscreen?.();
        this.isFullscreen = false;
      }
    },

    /* ── HELPERS DE COLOR / CHIP ── */
    colorCard(status) {
      return STATUS_COLORS[status] || STATUS_COLORS[2];
    },

    activeChipStyle(opcion) {
      const c = this.colorCard(opcion ?? 1);
      return {
        background: c + '15',
        color: c,
        borderColor: c + '50',
      };
    },

    contarFiltro(opcion) {
      if (opcion === null) return this.maquinas.length;
      return this.maquinas.filter(m => m.status === opcion).length;
    },

    /* ── HELPERS DE LECTURA (camel/Pascal) ── */
    val(obj, ...keys) {
      for (const key of keys) {
        if (obj && obj[key] !== undefined && obj[key] !== null) return obj[key];
      }
      return undefined;
    },

    arr(value) {
      return Array.isArray(value) ? value : [];
    },

    num(value, fallback = 0) {
      const n = Number(value);
      return Number.isFinite(n) ? n : fallback;
    },

    /* ── CARGA DE DATOS ── */
    getAreas() {
      axios.get('/Areas')
        .then(r => { this.areas = (r.data || []).filter(a => a.idArea > 1); })
        .catch(e => console.log(e));
    },

    async getInstancias() {
      try {
        const res = await axios.get('/MaquinaInstancias');
        this.instancias = res.data || [];
      } catch (e) { console.log(e); }
    },

    idsParaConsulta() {
      if (this.idArea > 1) {
        return (this.instancias || [])
          .filter(x => Number(this.val(x, 'idArea', 'IdArea')) === Number(this.idArea))
          .map(x => this.val(x, 'idMaquinaIns', 'IdMaquinaIns'))
          .filter(Boolean);
      }
      return [];
    },

    buildQuery(ids) {
      const params = new URLSearchParams();
      ids.forEach(id => params.append('idsMaquinasIns', id));
      return params.toString();
    },

    adaptMaquina(item) {
      const inf = this.val(item, 'Informador', 'informador') || null;
      const resumen = this.val(inf, 'ResumenArticulosProgresoTotal', 'resumenArticulosProgresoTotal') || {};
      const tieneOdt = this.val(item, 'TieneOperacionEnCurso', 'tieneOperacionEnCurso') === true;

      const articulos = this.arr(this.val(inf, 'ArticulosProgreso', 'articulosProgreso')).map(a => ({
        idArticulo: this.num(this.val(a, 'IdArticulo', 'idArticulo')),
        nombre: this.val(a, 'NombreArticulo', 'nombreArticulo') || '',
        codigo: this.val(a, 'CodigoArticulo', 'codigoArticulo') || '',
        reportadas: this.num(this.val(a, 'Reportadas', 'reportadas')),
        meta: this.num(this.val(a, 'Meta', 'meta')),
        reportadasNoOk: this.num(this.val(a, 'ReportadasNoOk', 'reportadasNoOk')),
      }));

      return {
        idMaquinaIns: this.val(item, 'IdMaquinaIns', 'idMaquinaIns'),
        codigoMaquina: this.val(item, 'NombreMaquina', 'nombreMaquina') || '',
        status: tieneOdt && inf ? 1 : 2,

        claveOdt: inf ? this.val(inf, 'ClaveOdt', 'claveOdt') : null,
        codigoMolde: inf ? this.val(inf, 'CodigoMolde', 'codigoMolde') : null,
        inicioODT: inf ? this.val(inf, 'InicioODT', 'inicioODT') : null,
        tiempoOperando: this.num(this.val(inf, 'TiempoOperando', 'tiempoOperando')),
        promedioRate: this.num(this.val(inf, 'PromedioRate', 'promedioRate')),

        meta: this.num(this.val(resumen, 'Meta', 'meta')),
        piezasOk: this.num(this.val(resumen, 'Reportadas', 'reportadas')),
        piezasNoOk: this.num(this.val(resumen, 'ReportadasNoOk', 'reportadasNoOk')),
        porcentajeScrap: this.num(this.val(resumen, 'PorcentajeScrap', 'porcentajeScrap')),
        cantidadArticulos: this.num(this.val(resumen, 'CantidadArticulos', 'cantidadArticulos')),

        articulos,
      };
    },

    // true solo si el componente sigue montado y activo (pestaña visible).
    puedeConsultar() {
      return this.montado && this.active;
    },

    async obtenerDatos() {
      if (this.loading || !this.puedeConsultar()) return;
      this.loading = true;
      try {
        const ids = this.idsParaConsulta();

        if (this.idArea > 1 && !ids.length) {
          this.maquinas = [];
          return;
        }

        const query = this.buildQuery(ids);
        const url = `/OEEV2/InformadorGeneralMaquinas${query ? `?${query}` : ''}`;
        const { data } = await axios.get(url);
        const items = Array.isArray(data) ? data : [];
        this.maquinas = items.map(item => this.adaptMaquina(item));
      } catch (e) { console.log(e); }
      finally { this.loading = false; }
    },

    /* ── COUNTDOWN ── */
    resetCountdown() {
      this.nextUpdateSeconds = Math.max(1, Math.round(this.pollMs / 1000));
    },

    iniciarCountdown() {
      if (this.intervalCountdown) return;
      this.resetCountdown();
      this.intervalCountdown = setInterval(() => {
        this.nextUpdateSeconds = Math.max(0, this.nextUpdateSeconds - 1);
      }, 1000);
    },

    detenerCountdown() {
      if (!this.intervalCountdown) return;
      clearInterval(this.intervalCountdown);
      this.intervalCountdown = null;
    },

    /* ── PAGINADO FULLSCREEN ── */
    normalizarPaginaActual() {
      if (this.currentPage >= this.totalPagesFullscreen) {
        this.currentPage = 0;
      }
    },

    avanzarPaginaFullscreen() {
      if (!this.isFullscreen || this.totalPagesFullscreen <= 1) return;
      this.currentPage = (this.currentPage + 1) % this.totalPagesFullscreen;
    },

    iniciarPaginadoFullscreen() {
      if (this.intervalPage || this.totalPagesFullscreen <= 1) return;
      this.intervalPage = setInterval(() => this.avanzarPaginaFullscreen(), 30000);
    },

    detenerPaginadoFullscreen() {
      if (!this.intervalPage) return;
      clearInterval(this.intervalPage);
      this.intervalPage = null;
    },

    /* ── CICLO ── */
    async actualizarTodo() {
      if (!this.puedeConsultar()) { this.detener(); return; }
      await this.obtenerDatos();
      if (!this.puedeConsultar()) return;
      this.resetCountdown();
      this.normalizarPaginaActual();
      this.iniciarPaginadoFullscreen();
    },

    async iniciar() {
      if (!this.puedeConsultar()) return;
      await this.getInstancias();
      if (!this.puedeConsultar()) return;
      this.getAreas();
      await this.actualizarTodo();
      if (!this.puedeConsultar() || this.interval) return;
      this.interval = setInterval(() => this.actualizarTodo(), this.pollMs);
      this.iniciarCountdown();
    },

    detener() {
      if (this.interval) { clearInterval(this.interval); this.interval = null; }
      this.detenerCountdown();
      this.detenerPaginadoFullscreen();
    },
  },
};
</script>

<style>
/* ===================================================
   TOKENS GLOBALES (sin scoped para fullscreen)
   =================================================== */
.maquina-estado-root {
  --clr-surface: #F0F2F6;
  --clr-border: rgba(0, 0, 0, 0.07);
  --clr-text-primary: #0D1117;
  --clr-text-secondary: #5A6476;
  --clr-text-muted: #8A95A3;
  --font-primary: 'Space Grotesk', 'Roboto', sans-serif;
  background: #F4F6F9;
  transition: all 0.4s ease;
}

/* ===================================================
   TOOLBAR
   =================================================== */
.toolbar-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 12px;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toolbar-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: var(--font-primary);
  font-size: 15px;
  font-weight: 600;
  color: var(--clr-text-primary);
}

.machine-count-pill {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  background: var(--clr-surface);
  border: 1px solid var(--clr-border);
  font-family: var(--font-primary);
  font-size: 11px;
  font-weight: 600;
  color: var(--clr-text-secondary);
}

.fs-btn {
  display: inline-flex;
  align-items: center;
  padding: 8px 14px;
  border-radius: 10px;
  border: 1.5px solid rgba(0, 0, 0, 0.15);
  background: #ffffff;
  font-family: var(--font-primary);
  font-size: 12px;
  font-weight: 600;
  color: #3d4a5c;
  cursor: pointer;
  transition: background 0.18s, border-color 0.18s, box-shadow 0.18s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  white-space: nowrap;
  outline: none;
}

.fs-btn:hover {
  background: #f4f6f9;
  border-color: rgba(0, 0, 0, 0.25);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.10);
}

.fs-btn:active {
  transform: scale(0.97);
}

.fs-btn .v-icon {
  color: #5A6476 !important;
}

@media (max-width: 600px) {
  .fs-btn-label {
    display: none;
  }

  .fs-btn {
    padding: 8px 10px;
  }
}

/* ===================================================
   DASHBOARD CARD
   =================================================== */
.dashboard-card.v-card {
  background: transparent !important;
  border: none !important;
}

.dashboard-body.v-card__text {
  padding: 0 12px 24px !important;
}

/* ===================================================
   AREA SELECT
   =================================================== */
.area-select.v-input {
  background: #fff;
  border-radius: 10px;
  font-family: var(--font-primary) !important;
  font-size: 13px !important;
}

/* ===================================================
   FILTER CHIPS
   =================================================== */
.filter-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border-radius: 30px;
  border: 1.5px solid var(--clr-border);
  background: #fff;
  font-family: var(--font-primary);
  font-size: 12px;
  font-weight: 500;
  color: var(--clr-text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;
}

.filter-chip:hover {
  border-color: rgba(0, 0, 0, 0.18);
  background: var(--clr-surface);
}

.chip-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}

.chip-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: var(--clr-surface);
  font-size: 10px;
  font-weight: 700;
  color: var(--clr-text-muted);
  margin-left: 2px;
}

.filter-chip.active .chip-count {
  background: #fff;
  color: inherit;
  opacity: 0.95;
}

/* ===================================================
   LOADING
   =================================================== */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  gap: 16px;
}

.loading-text {
  font-family: var(--font-primary);
  font-size: 14px;
  font-weight: 500;
  color: var(--clr-text-muted);
}

/* ===================================================
   FULLSCREEN
   =================================================== */
.maquina-estado-root:fullscreen {
  padding: 20px;
  overflow: hidden;
  height: 100vh;
  background: #F4F6F9;
}

.maquina-estado-root:fullscreen .dashboard-body.v-card__text {
  max-height: calc(100vh - 96px);
  overflow: hidden;
}

/* ===================================================
   RESPONSIVE
   =================================================== */
@media (max-width: 600px) {
  .toolbar-row {
    padding: 12px 14px 8px;
  }

  .toolbar-title {
    font-size: 13px;
  }

  .filter-chip {
    font-size: 11px;
    padding: 5px 10px;
  }

  .dashboard-body.v-card__text {
    padding: 0 6px 16px !important;
  }
}

@media (min-width: 3200px) {
  .toolbar-title {
    font-size: 1.4rem;
  }

  .filter-chip {
    font-size: 1rem;
    padding: 10px 20px;
  }

  .machine-count-pill {
    font-size: 0.95rem;
  }
}
</style>
